import React, {forwardRef, useState} from 'react'

const Checkbox = forwardRef(function Checkbox (props, ref) {
  const { label, checked: incomingChecked = false, name, ...attrs } = props
  const [checked, setChecked] = useState(incomingChecked)

  const onChange = (evt) => {
    setChecked(evt.target.checked)
  }

  return (
    <label className="flex items-center w-fit leading-none translate-x-1">
      <input type="hidden" name={checked ? '' : name} value="off" />
      <input name={checked ? name : ''} checked={checked} {...attrs} className="mr-3 -translate-0.5 scale-150 shadow" ref={ref} onChange={onChange} />
      {label}
    </label>
  )
})

const TextInput = forwardRef(function TextInput (props, ref) {
  const { className, label, value, disabled, icon, ...attrs } = props

  const classes = [
    'group',
    'text-slate-300',
    'border-b',
    'border-slate-500',
    'focus-within:border-slate-300',
    'autofill:bg-transparent',
    'w-full'
  ]

  return (
    <>
      {icon ? <div className={`${classes.join(' ')} flex gap-2 items-center`}>
          <div
            className="w-fit flex items-center -translate-y-0.5">{React.createElement(icon, {className: 'size-6'})}</div>
        <input ref={ref} type="text" {...attrs} defaultValue={value} className={`outline-none w-full ${className}`} />
      </div> :
        <div className={`${classes.join(' ')} flex gap-2`}>
          <input ref={ref} type="text" {...attrs} defaultValue={value} className={`outline-none ${className}`} />
        </div>
      }
    </>
  )
})

const TextArea = forwardRef(function TextArea (props, ref) {
  const { label, value: valFromProps, className, border = true, ...attrs } = props
  const [value, setValue] = useState(valFromProps || '')

  const handleChange = (evt) => {
    if (props.onChange) { props.onChange(evt) }
    setValue(evt.target.value)
  }

  return (
    <>
      <label htmlFor={name} className="w-full">{label}</label>
      <textarea {...attrs} ref={ref} value={value} onChange={handleChange} className={`bg-white outline-none w-full p-2 block ${border ? 'border border-black rounded' : ''} flex flex-col flex-grow ${className}`} />
    </>
  )
})

const FileInput = forwardRef(function FileInput (props, ref) {
  const { label, ...attrs } = props
  return (
    <>
      <div>
        <label htmlFor={name} className="w-full">{label}</label>
      </div>
      <div>
        <input {...attrs} type="file" ref={ref} />
      </div>
    </>
  )
})

const PasswordInput = forwardRef(function PasswordInput (props, ref) {
  const { ...attrs } = props
  return <TextInput ref={ref} type="password" {...attrs} />
})

const HiddenInput = forwardRef(function HiddenInput (props, ref) {
  return (
    <input ref={ref} type="hidden" {...props} />
  )
})

const Select = forwardRef(function Select (props, ref) {
  const { options, label, select, ...attrs } = props

  return <>
    <div>
      <label htmlFor={name} className="w-full">{label}</label>
    </div>
    <div>
      <select {...attrs} name={name} ref={ref} className="border border-black w-full p-2 rounded">
        {options.map(({ label, value, selected }, i) => <option key={i} value={value} selected={selected}>{label}</option>)}
      </select>
    </div>
  </>
})

export const Field = forwardRef(function Field (props, ref) {
  const { errors, label, note, ...attrs } = props

  let component
  switch (attrs.type) {
    case 'checkbox':
      component = <Checkbox ref={ref} label={label} {...attrs} />
      break
    case 'textarea':
      component = <TextArea ref={ref} label={label} {...attrs} />
      break
    case 'text':
      component = <TextInput ref={ref} label={label} {...attrs} />
      break
    case 'file':
      component = <FileInput ref={ref} label={label} {...attrs} />
      break
    case 'files':
      component = <FileInput multiple ref={ref} label={label} {...attrs} />
      break
    case 'password':
      component = <PasswordInput ref={ref} label={label} {...attrs} />
      break
    case 'select':
      component = <Select ref={ref} label={label} {...attrs} />
      break
    case 'hidden':
      component = <HiddenInput ref={ref} {...attrs} />
      return component // No need for rendering below with more layout
  }

  return (
    <div>
      {component}
      {errors?.length > 0 && <ul>
        {errors.map((err, i) => <li key={i}>{err}</li>)}
      </ul>}
      {note && <div className="text-sm italic text-slate-400">{note}</div>}
    </div>
  )
})
